/**
 * Rosetta Context Loader
 * Loads ROSETTA.md and module files for AI agent context
 */

import fs from 'fs';
import path from 'path';

export interface RosettaContext {
  hasRosetta: boolean;
  rootContext: string;
  modules: Map<string, string>;
  notes: string[];
  lastUpdated?: string;
}

export interface ModuleInfo {
  name: string;
  path: string;
  description: string;
  loadWhen: string;
}

export class RosettaLoader {
  private workingDirectory: string;
  private rosettaPath: string;
  private rosettaDir: string;
  private cachedContext: RosettaContext | null = null;
  private cacheTime: number = 0;
  private readonly CACHE_TTL = 30000; // 30 seconds

  constructor(workingDirectory: string) {
    this.workingDirectory = workingDirectory;
    this.rosettaPath = path.join(workingDirectory, 'ROSETTA.md');
    this.rosettaDir = path.join(workingDirectory, '.rosetta');
  }

  /**
   * Check if Rosetta is initialized in the project
   */
  hasRosetta(): boolean {
    return fs.existsSync(this.rosettaPath);
  }

  /**
   * Load the root ROSETTA.md context
   */
  loadRootContext(): string {
    if (!fs.existsSync(this.rosettaPath)) {
      return '';
    }

    try {
      return fs.readFileSync(this.rosettaPath, 'utf-8');
    } catch (error) {
      return '';
    }
  }

  /**
   * Parse module index from ROSETTA.md
   */
  parseModuleIndex(content: string): ModuleInfo[] {
    const modules: ModuleInfo[] = [];
    const lines = content.split('\n');
    let inModuleIndex = false;
    let headerPassed = false;

    for (const line of lines) {
      if (line.includes('## Module Index')) {
        inModuleIndex = true;
        continue;
      }

      if (inModuleIndex && line.startsWith('##')) {
        break;
      }

      if (inModuleIndex) {
        if (line.includes('| Module |') || line.includes('|---')) {
          headerPassed = true;
          continue;
        }

        if (headerPassed && line.startsWith('|')) {
          const cells = line.split('|').map(c => c.trim()).filter(c => c);
          if (cells.length >= 4 && cells[0] && cells[1]) {
            modules.push({
              name: cells[0],
              path: cells[1].replace(/`/g, ''),
              description: cells[2] || '',
              loadWhen: cells[3] || '',
            });
          }
        }
      }
    }

    return modules;
  }

  /**
   * Load a specific module file
   */
  loadModule(modulePath: string): string {
    const fullPath = path.join(this.workingDirectory, modulePath);
    if (!fs.existsSync(fullPath)) {
      return '';
    }

    try {
      return fs.readFileSync(fullPath, 'utf-8');
    } catch (error) {
      return '';
    }
  }

  /**
   * Load recent agent notes
   */
  loadRecentNotes(limit: number = 5): string[] {
    const notesPath = path.join(this.rosettaDir, 'notes.md');
    if (!fs.existsSync(notesPath)) {
      return [];
    }

    try {
      const content = fs.readFileSync(notesPath, 'utf-8');
      const notes: string[] = [];
      const lines = content.split('\n');
      let currentNote: string[] = [];

      for (const line of lines) {
        if (line.match(/^###\s+\d{4}-\d{2}-\d{2}/)) {
          if (currentNote.length > 0) {
            notes.push(currentNote.join('\n'));
          }
          currentNote = [line];
        } else if (currentNote.length > 0 && line.match(/^-\s+/)) {
          currentNote.push(line);
        }
      }

      if (currentNote.length > 0) {
        notes.push(currentNote.join('\n'));
      }

      // Return most recent notes
      return notes.slice(-limit);
    } catch (error) {
      return [];
    }
  }

  /**
   * Append a note to the notes file
   */
  appendNote(note: string, agentName: string = 'metis-code'): boolean {
    const notesPath = path.join(this.rosettaDir, 'notes.md');
    if (!fs.existsSync(notesPath)) {
      return false;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const noteEntry = `\n### ${today} | ${agentName}\n- ${note}\n`;

      let content = fs.readFileSync(notesPath, 'utf-8');
      content = content.trimEnd() + noteEntry;
      fs.writeFileSync(notesPath, content, 'utf-8');
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Find relevant modules based on task keywords
   */
  findRelevantModules(task: string, modules: ModuleInfo[]): ModuleInfo[] {
    const taskLower = task.toLowerCase();
    const relevant: ModuleInfo[] = [];

    for (const module of modules) {
      // Check if task matches "Load When" description
      const loadWhenLower = module.loadWhen.toLowerCase();
      const descLower = module.description.toLowerCase();
      const nameLower = module.name.toLowerCase();

      // Simple keyword matching
      const keywords = [
        ...loadWhenLower.split(/[\s,]+/),
        ...descLower.split(/[\s,]+/),
        nameLower,
      ].filter(k => k.length > 2);

      for (const keyword of keywords) {
        if (taskLower.includes(keyword)) {
          relevant.push(module);
          break;
        }
      }
    }

    return relevant;
  }

  /**
   * Load full Rosetta context for a task
   */
  loadContext(task?: string): RosettaContext {
    // Check cache
    if (this.cachedContext && (Date.now() - this.cacheTime) < this.CACHE_TTL) {
      return this.cachedContext;
    }

    const context: RosettaContext = {
      hasRosetta: this.hasRosetta(),
      rootContext: '',
      modules: new Map(),
      notes: [],
    };

    if (!context.hasRosetta) {
      return context;
    }

    // Load root context
    context.rootContext = this.loadRootContext();

    // Extract last updated date
    const lastUpdatedMatch = context.rootContext.match(/<!-- rosetta:last-updated:(\d{4}-\d{2}-\d{2}) -->/);
    if (lastUpdatedMatch) {
      context.lastUpdated = lastUpdatedMatch[1];
    }

    // Parse module index
    const moduleIndex = this.parseModuleIndex(context.rootContext);

    // If task provided, load relevant modules
    if (task) {
      const relevantModules = this.findRelevantModules(task, moduleIndex);
      for (const module of relevantModules) {
        const moduleContent = this.loadModule(module.path);
        if (moduleContent) {
          context.modules.set(module.name, moduleContent);
        }
      }
    }

    // Load recent notes
    context.notes = this.loadRecentNotes();

    // Cache the context
    this.cachedContext = context;
    this.cacheTime = Date.now();

    return context;
  }

  /**
   * Generate context string for system prompt
   */
  generateContextPrompt(task?: string): string {
    const context = this.loadContext(task);

    if (!context.hasRosetta) {
      return '';
    }

    let prompt = '\n\n=== ROSETTA CODEBASE CONTEXT ===\n';
    prompt += 'This project uses Rosetta for AI agent context management.\n\n';

    // Add root context (abbreviated for token efficiency)
    if (context.rootContext) {
      // Extract key sections only
      const sections = this.extractKeySections(context.rootContext);
      prompt += sections;
    }

    // Add loaded modules
    if (context.modules.size > 0) {
      prompt += '\n--- RELEVANT MODULE CONTEXT ---\n';
      for (const [name, content] of context.modules) {
        prompt += `\n[Module: ${name}]\n`;
        prompt += this.extractKeySections(content);
      }
    }

    // Add recent notes
    if (context.notes.length > 0) {
      prompt += '\n--- RECENT AGENT LEARNINGS ---\n';
      prompt += context.notes.join('\n');
      prompt += '\n';
    }

    prompt += '\n=== END ROSETTA CONTEXT ===\n';
    prompt += 'If you discover something valuable about this codebase, you can append it to .rosetta/notes.md\n';

    return prompt;
  }

  /**
   * Extract key sections from Rosetta content for token efficiency
   */
  private extractKeySections(content: string): string {
    const lines = content.split('\n');
    const sections: string[] = [];
    let currentSection: string[] = [];
    let inCodeBlock = false;
    let skipSection = false;

    for (const line of lines) {
      // Track code blocks
      if (line.startsWith('```')) {
        inCodeBlock = !inCodeBlock;
      }

      // Detect section headers
      if (line.startsWith('## ') && !inCodeBlock) {
        // Save previous section
        if (currentSection.length > 0 && !skipSection) {
          sections.push(currentSection.join('\n'));
        }

        // Check if this section should be skipped (verbose sections)
        const sectionName = line.replace('## ', '').toLowerCase();
        skipSection = ['agent notes'].includes(sectionName);

        currentSection = [line];
      } else {
        currentSection.push(line);
      }
    }

    // Add last section
    if (currentSection.length > 0 && !skipSection) {
      sections.push(currentSection.join('\n'));
    }

    return sections.join('\n\n');
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cachedContext = null;
    this.cacheTime = 0;
  }
}

// Global instance cache
const loaderCache = new Map<string, RosettaLoader>();

export function getRosettaLoader(workingDirectory: string): RosettaLoader {
  if (!loaderCache.has(workingDirectory)) {
    loaderCache.set(workingDirectory, new RosettaLoader(workingDirectory));
  }
  return loaderCache.get(workingDirectory)!;
}
